// Each session's own text size (⌘+ / ⌘− / ⌘0 on the focused pane, as Ghostty and cmux zoom one surface): steps from
// the default size, kept per session on this device, so a row, a tab or a reload keeps it. The default moving (a phone's
// narrower screen) doesn't undo a zoom: the steps ride on top of it.

const KEY = 'cmx.zoom';
const MIN = -4;
const MAX = 12;
/** Sessions remembered at most: the oldest go first. */
const KEEP = 200;

let steps: Record<string, number> = load();
const listeners = new Set<(sid: number, percent: number) => void>();

function load(): Record<string, number> {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '{}');
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

function save() {
  const keys = Object.keys(steps);
  if (keys.length > KEEP) for (const k of keys.slice(0, keys.length - KEEP)) delete steps[k];
  try {
    localStorage.setItem(KEY, JSON.stringify(steps));
  } catch {
    // private window: it lasts until the page goes
  }
}

/** The session's steps from the default (0 unless zoomed). */
export const zoomSteps = (sid: number) => steps[sid] ?? 0;

/** `by` steps bigger (+1), smaller (−1), or back to the default (0); says the new size as a share of the default. */
export function zoom(sid: number, by: -1 | 0 | 1, base: number) {
  const now = by === 0 ? 0 : Math.min(MAX, Math.max(MIN, zoomSteps(sid) + by));
  const { [sid]: _, ...rest } = steps;
  steps = now ? { ...rest, [sid]: now } : rest; // the latest last, so the oldest are the ones dropped
  save();
  const percent = Math.round(((base + now) / base) * 100);
  for (const f of listeners) f(sid, percent);
}

/** Called with the session and its new size (as a percentage) each time one zooms. */
export function onZoom(f: (sid: number, percent: number) => void) {
  listeners.add(f);
  return () => void listeners.delete(f);
}
